import React from 'react';
import SVGIcon from '../SVGIcon/SVGIcon';
import { styles } from './TextInput.style';

/* value is left out: the component always sets the input's value from inputValue */
type TextInputAttributes = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;

interface TextInputProps extends TextInputAttributes {
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className?: string;
  type?: string;
  invalid?: boolean;
  validated?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Add an element to the left of the input */
  leftElement?: React.ReactElement | null;
  /** Add an element to the right of the input */
  rightElement?: React.ReactElement | null;
  /** Input value passed on by wrapping container */
  inputValue?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  ref?: React.Ref<HTMLInputElement>;
}

/**
 * The TextInput component represents the standard html element `<input />` with adjusted styling.
 * This component gives the possibility to add extra elements as an icon or button to improve the functionality of the input element.
 */

const TextInput = ({
  className = '',
  validated = false,
  invalid = false,
  leftElement = null,
  rightElement = null,
  inputValue = '',
  onChange = () => {},
  placeholder = '',
  type = 'text',
  ref,
  ...inputAttributes
}: TextInputProps) => {
  const rightElementButtonChild =
    rightElement &&
    typeof rightElement.type !== 'string' &&
    rightElement.type.name === 'Button';
  const showRightContentWrapper = rightElement || validated;
  return (
    <div tw="relative">
      {leftElement && <span css={styles.leftElement}>{leftElement}</span>}
      <input
        {...inputAttributes}
        type={type}
        onChange={onChange}
        ref={ref}
        placeholder={placeholder}
        value={inputValue}
        css={styles.defaultInput({ $invalid: invalid, leftElement, rightElement })}
        className={className}
      />
      {showRightContentWrapper && (
        <div css={styles.rightContentWrapper}>
          {validated && (
            <span css={styles.rightElement({ rightElementButtonChild })}>
              <SVGIcon icon="check" tw="text-green-darken-10" />
            </span>
          )}
          {rightElement && (
            <span css={styles.rightElement({ rightElementButtonChild })}>
              {rightElement}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TextInput;
